import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Filter } from "lucide-react";
import api, { resolveImage } from "@/lib/api";

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get("kategori") || "";
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({ hero_eyebrow: "Ürün Kataloğu", hero_title: "Tüm Ürünler" });

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data || []));
    api.get("/page-content/products").then(({ data }) => setContent(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (selectedCategory) params.category = selectedCategory;
    if (search) params.search = search;
    api
      .get("/products", { params })
      .then(({ data }) => setProducts(data?.items || []))
      .finally(() => setLoading(false));
  }, [selectedCategory, search]);

  const handleCategory = (slug) => {
    if (slug) setSearchParams({ kategori: slug });
    else setSearchParams({});
  };

  const currentCat = categories.find((c) => c.slug === selectedCategory);

  return (
    <div data-testid="products-page">
      <section className="bg-[#0A2540] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <div className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-3">
            {currentCat ? "Kategori" : content.hero_eyebrow}
          </div>
          <h1 className="heading-font text-4xl md:text-5xl lg:text-6xl max-w-4xl leading-[1.05]">
            {currentCat ? currentCat.name : content.hero_title}
          </h1>
          {currentCat && (
            <p className="mt-6 text-white/70 max-w-2xl">{currentCat.description}</p>
          )}
        </div>
      </section>

      <section className="py-12 bg-white border-b border-slate-200 sticky top-24 z-30 backdrop-blur-xl bg-white/90">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Ürün ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none text-sm"
              data-testid="products-search-input"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Filter size={14} />
            {products.length} ürün
          </div>
        </div>
      </section>

      <section className="py-12 bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-3" data-testid="categories-sidebar">
            <div className="sticky top-44">
              <h3 className="heading-font text-sm uppercase tracking-wider text-slate-500 mb-4">
                Kategoriler
              </h3>
              <div className="space-y-1">
                <button
                  onClick={() => handleCategory("")}
                  className={`w-full text-left px-4 py-3 text-sm border-l-2 transition-colors ${
                    !selectedCategory
                      ? "border-[#0066FF] bg-white text-[#0066FF] font-semibold"
                      : "border-transparent hover:bg-white text-slate-700"
                  }`}
                  data-testid="filter-category-all"
                >
                  Tüm Kategoriler
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleCategory(c.slug)}
                    className={`w-full text-left px-4 py-3 text-sm border-l-2 transition-colors ${
                      selectedCategory === c.slug
                        ? "border-[#0066FF] bg-white text-[#0066FF] font-semibold"
                        : "border-transparent hover:bg-white text-slate-700"
                    }`}
                    data-testid={`filter-category-${c.slug}`}
                  >
                    <span>{c.name}</span>
                    <span className="text-xs text-slate-400 ml-2">({c.product_count})</span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            {loading ? (
              <div className="text-center py-20 text-slate-500" data-testid="products-loading">
                Yükleniyor...
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 text-slate-500 bg-white border border-slate-200" data-testid="no-products">
                Bu kategoride henüz ürün bulunmuyor.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((p) => (
                  <Link
                    key={p.id}
                    to={`/urun/${p.slug}`}
                    className="group bg-white border border-slate-200 hover:border-[#0066FF] transition-colors"
                    data-testid={`product-card-${p.slug}`}
                  >
                    <div className="aspect-square overflow-hidden bg-slate-100">
                      <img
                        src={resolveImage(p.image_url)}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="text-xs uppercase tracking-wider text-[#0066FF] font-semibold mb-2">
                        {categories.find((c) => c.slug === p.category_slug)?.name?.split(" ")[0] || "Ürün"}
                      </div>
                      <h3 className="heading-font text-base text-[#0A2540] mb-2 group-hover:text-[#0066FF] transition-colors">
                        {p.name}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{p.short_description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
